import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const isAtMaximum = effectiveMax !== null && value >= effectiveMax;

    // At the minimum, minus removes the cart line instead of becoming disabled.
    minusButton.disabled = false;
    // Keep the plus control clickable at the limit so it can explain why
    // the quantity did not increase. aria-disabled preserves its visual state.
    plusButton.disabled = false;
    plusButton.setAttribute('aria-disabled', isAtMaximum.toString());
  }

  /**
   * Blocks increases above the cart limit and displays the merchant message.
   * @param {Event} event
   */
  increaseQuantity(event) {
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (effectiveMax !== null && value >= effectiveMax) {
      event.preventDefault();
      this.showQuantityLimitMessage();
      return;
    }

    this.hideQuantityLimitMessage();
    super.increaseQuantity(event);
  }

  /** @param {Event} event */
  decreaseQuantity(event) {
    this.hideQuantityLimitMessage();

    const { min, value } = this.getCurrentValues();
    if (value <= min) {
      event.preventDefault();

      const line = Number(this.refs.quantityInput.dataset.cartLine);
      const cartItems = this.closest('cart-items-component');
      if (line && cartItems && 'onLineItemRemove' in cartItems) {
        cartItems.onLineItemRemove(line);
      }
      return;
    }

    super.decreaseQuantity(event);
  }

  showQuantityLimitMessage() {
    const message = this.closest('.cart-items__quantity-controls')?.querySelector('[data-quantity-limit-message]');
    message?.removeAttribute('hidden');
  }

  hideQuantityLimitMessage() {
    const message = this.closest('.cart-items__quantity-controls')?.querySelector('[data-quantity-limit-message]');
    message?.setAttribute('hidden', '');
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
